import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { MessageCircle, Phone, MapPin, CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";
import { AGE_GROUPS, STATUSES, INTERESTS } from "../../data/site";
import { API, waLink, PHONE_TEL, PHONE_DISPLAY, ADDRESS, getAttribution, PRIVACY_POLICY_VERSION, TERMS_VERSION } from "../../lib/contact";

const EMPTY = { name: "", mobile: "", whatsapp: "", city: "", email: "", message: "", age_group: "", status: "", interest: "" };

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-background px-4 py-3.5 text-sm text-navy placeholder:text-slate-400 outline-none focus:border-electric focus:ring-2 focus:ring-electric/15 transition-all";

const Field = ({ label, optional, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
      {label} {optional && <span className="text-slate-400 normal-case font-normal">(optional)</span>}
    </span>
    {children}
  </label>
);

const normalizeMobileClient = (raw) => {
  let d = (raw || "").replace(/\D/g, "");
  if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
  else if (d.length === 11 && d.startsWith("0")) d = d.slice(1);
  return d;
};

export const LeadForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [consent, setConsent] = useState(false);
  const [submissionId, setSubmissionId] = useState(() => crypto.randomUUID());
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    if (form.name.trim().length < 2) return "Please enter your full name.";
    if (!/^[6-9]\d{9}$/.test(normalizeMobileClient(form.mobile))) return "Please enter a valid 10-digit Indian mobile number.";
    if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) return "Please enter a valid email address.";
    if (!form.city.trim()) return "Please enter your city.";
    if (!consent) return "Please accept the Privacy Policy and Terms & Conditions to continue.";
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        name: form.name.trim(),
        city: form.city.trim(),
        email: form.email.trim() || null,
        message: form.message.trim() || null,
        consent_given: consent,
        consent_timestamp: new Date().toISOString(),
        privacy_policy_version: PRIVACY_POLICY_VERSION,
        terms_version: TERMS_VERSION,
        website_submission_id: submissionId,
        company: "",
        ...getAttribution(),
      });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again or message us on WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="lead-form-section" className="py-24 sm:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <SectionHead
              overline="Free Counselling"
              title="Not Sure Which Online Business Model Is Right for You?"
              sub="Book a free counselling session at LaunchX Academy. We'll understand your interests, experience, available investment and goals before helping you select a suitable learning path."
            />
            <div className="mt-10 space-y-4">
              {[
                { icon: MessageCircle, label: "WhatsApp Admissions", value: PHONE_DISPLAY, href: waLink(), testid: "contact-whatsapp-card" },
                { icon: Phone, label: "Call the Academy", value: PHONE_DISPLAY, href: `tel:${PHONE_TEL}`, testid: "contact-phone-card" },
                { icon: MapPin, label: "Visit Us", value: ADDRESS, href: null, testid: "contact-address-card" },
              ].map((c) => {
                const Inner = (
                  <>
                    <span className="w-11 h-11 rounded-xl bg-navy flex items-center justify-center shrink-0">
                      <c.icon size={17} className="text-cyanx" />
                    </span>
                    <span>
                      <span className="block text-[11px] uppercase tracking-[0.18em] text-slate-400">{c.label}</span>
                      <span className="block mt-0.5 font-display font-semibold text-navy text-sm">{c.value}</span>
                    </span>
                  </>
                );
                return (
                  <Reveal key={c.label}>
                    {c.href ? (
                      <a data-testid={c.testid} href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="flex items-center gap-4 bg-background border border-slate-200/80 rounded-2xl p-5 hover:border-electric/40 hover:shadow-lg transition-all">
                        {Inner}
                      </a>
                    ) : (
                      <div data-testid={c.testid} className="flex items-center gap-4 bg-background border border-slate-200/80 rounded-2xl p-5">{Inner}</div>
                    )}
                  </Reveal>
                );
              })}
            </div>
          </div>

          <Reveal delay={0.15}>
            <div className="bg-background border border-slate-200/80 rounded-3xl p-7 sm:p-9 shadow-sm" data-testid="lead-form-card">
              <AnimatePresence mode="wait">
                {done ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-10"
                    data-testid="lead-form-success"
                  >
                    <span className="mx-auto w-16 h-16 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                      <CheckCircle2 size={28} className="text-emerald-500" />
                    </span>
                    <h3 className="mt-6 font-display font-bold text-navy text-xl">Thank you!</h3>
                    <p className="mt-3 text-sm text-slate-500 leading-relaxed max-w-sm mx-auto">
                      Your enquiry has been received. Our LaunchX Academy team will contact you shortly.
                    </p>
                    <a
                      data-testid="lead-form-whatsapp-continue"
                      href={waLink(`Hi LaunchX Academy, I'm ${form.name} from ${form.city}. I just booked a counselling session for the 45-Day Practical Online Business Program.`)}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-7 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1fb857] text-white font-semibold text-sm rounded-full px-7 py-4 transition-all hover:-translate-y-0.5"
                    >
                      <MessageCircle size={16} /> Continue on WhatsApp
                    </a>
                  </motion.div>
                ) : (
                  <motion.form key="form" exit={{ opacity: 0, y: -12 }} onSubmit={submit} className="space-y-5" data-testid="lead-enquiry-form">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <Field label="Full Name" testid="lead-form-name-field">
                        <input data-testid="lead-form-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
                      </Field>
                      <Field label="City" testid="lead-form-city-field">
                        <input data-testid="lead-form-city-input" required value={form.city} onChange={set("city")} placeholder="Your city" className={inputCls} />
                      </Field>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <Field label="Mobile Number" testid="lead-form-mobile-field">
                        <input data-testid="lead-form-mobile-input" required type="tel" value={form.mobile} onChange={set("mobile")} placeholder="10-digit mobile" className={inputCls} />
                      </Field>
                      <Field label="WhatsApp Number" testid="lead-form-whatsapp-field">
                        <input data-testid="lead-form-whatsapp-input" type="tel" value={form.whatsapp} onChange={set("whatsapp")} placeholder="WhatsApp number" className={inputCls} />
                      </Field>
                    </div>
                    <Field label="Email Address" optional testid="lead-form-email-field">
                      <input data-testid="lead-form-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
                    </Field>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <Field label="Age Group" testid="lead-form-age-field">
                        <select data-testid="lead-form-age-select" value={form.age_group} onChange={set("age_group")} className={inputCls}>
                          <option value="" disabled>Select age group</option>
                          {AGE_GROUPS.map((a) => <option key={a} value={a}>{a}</option>)}
                        </select>
                      </Field>
                      <Field label="Current Status" testid="lead-form-status-field">
                        <select data-testid="lead-form-status-select" value={form.status} onChange={set("status")} className={inputCls}>
                          <option value="" disabled>Select status</option>
                          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </Field>
                    </div>
                    <Field label="What do you want to learn?" testid="lead-form-interest-field">
                      <select data-testid="lead-form-interest-select" value={form.interest} onChange={set("interest")} className={inputCls}>
                        <option value="" disabled>Select your goal</option>
                        {INTERESTS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </Field>
                    <Field label="Message" optional testid="lead-form-message-field">
                      <textarea data-testid="lead-form-message-input" rows={3} value={form.message} onChange={set("message")} placeholder="Anything you'd like us to know before the call?" className={`${inputCls} resize-none`} />
                    </Field>

                    <input type="text" name="company" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" onChange={() => {}} />

                    <label className="flex items-start gap-3 cursor-pointer" data-testid="lead-form-consent-field">
                      <input
                        data-testid="lead-form-consent-checkbox"
                        type="checkbox"
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                        className="mt-1 w-4 h-4 rounded border-slate-300 text-electric focus:ring-electric/30 accent-electric cursor-pointer"
                      />
                      <span className="text-xs text-slate-500 leading-relaxed">
                        I agree to the{" "}
                        <Link data-testid="lead-form-privacy-link" to="/privacy-policy" target="_blank" className="text-electric font-semibold hover:underline">Privacy Policy</Link>
                        {" "}and{" "}
                        <Link data-testid="lead-form-terms-link" to="/terms-and-conditions" target="_blank" className="text-electric font-semibold hover:underline">Terms &amp; Conditions</Link>
                        {" "}and consent to LaunchX Academy contacting me regarding courses, counselling, admissions, and related services.
                      </span>
                    </label>

                    {error && (
                      <p data-testid="lead-form-error" className="text-xs font-semibold text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5">{error}</p>
                    )}

                    <button
                      data-testid="lead-form-submit-button"
                      type="submit"
                      disabled={loading}
                      className="group w-full inline-flex items-center justify-center gap-2 bg-electric hover:bg-electric-dark disabled:opacity-60 text-white font-semibold text-sm rounded-xl px-7 py-4 transition-all hover:shadow-lg hover:shadow-electric/30"
                    >
                      {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />}
                      {loading ? "Submitting..." : "Book My Counselling Session"}
                    </button>
                    <p className="text-[11px] text-slate-400 text-center leading-relaxed">
                      By submitting, you agree to our{" "}
                      <Link to="/privacy-policy" target="_blank" className="text-electric hover:underline">Privacy Policy</Link> and{" "}
                      <Link to="/terms-and-conditions" target="_blank" className="text-electric hover:underline">Terms &amp; Conditions</Link>.
                    </p>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
